/**
 * Group-scoped facilitator surface. Entry point for a facilitator (or admin):
 * manage the group's teams, role caps, and members of teams; start a story for
 * a team that hasn't begun one; and drill into a story's session-release
 * dashboard. Membership of the GROUP is admin-only and shown read-only here.
 *
 * Keyed on the GROUP (/facilitate/group/:groupId), not a story — authority is
 * the group :facilitator role. See
 * plans/2026-06-21-group-scoped-facilitator-surface.md.
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container, Stack, Title, Text, Group, Button, Loader, Center, Paper,
  NumberInput, Switch, Divider, Select,
} from '@mantine/core'
import { IconUsersPlus, IconPlayerPlay, IconCalendarEvent, IconArrowRight } from '@tabler/icons-react'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { notifications } from '@mantine/notifications'
import { facilitatorService } from '../services/facilitatorService'
import { teamService } from '../services/teamService'
import { facilitatedStoriesAtom } from '../atoms/authAtoms'
import { TeamManagementPanel } from '../components/teams/TeamManagementPanel'

export default function FacilitatorGroup() {
  const { groupId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const groupKey = ['facilitatorGroup', groupId]
  const { data: group, isLoading, isError } = useQuery({
    queryKey: groupKey,
    queryFn: () => facilitatorService.getGroup(groupId),
  })

  // Full detail (members, caps, claims) per team — drives TeamManagementPanel.
  const teamSummaries = group?.teams || []
  const detailQueries = useQueries({
    queries: teamSummaries.map((t) => ({
      queryKey: ['team', t.id],
      queryFn: () => teamService.getTeam(t.id),
    })),
  })
  const teamDetails = detailQueries.map((q) => q.data).filter(Boolean)

  const stories = group?.stories || []

  // Stories the facilitator can open the thread-gates screen for. We do NOT
  // gate this on group.stories alone: that live fetch can come back empty
  // (transient, or a missing group↔story link), which would make the thread
  // gates page unreachable from the UI. Fall back to the facilitator's cached
  // facilitated_stories scoped to THIS group so the link is always present
  // when they actually have a story to gate. De-duped by id, group stories first.
  const facilitatedStories = useAtomValue(facilitatedStoriesAtom)
  const gateableStories = (() => {
    const byId = new Map()
    for (const s of stories) byId.set(s.id, { id: s.id, name: s.name })
    for (const s of facilitatedStories) {
      if (String(s.group_id) === String(groupId) && !byId.has(s.id)) {
        byId.set(s.id, { id: s.id, name: s.name })
      }
    }
    return [...byId.values()]
  })()

  // Create teams (optionally without starting a story).
  const [count, setCount] = useState(2)
  const [distribute, setDistribute] = useState(true)
  const create = useMutation({
    mutationFn: () => teamService.batchCreateTeams({ groupId, count, distribute }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKey })
      notifications.show({ color: 'green', message: `Created ${count} team(s)` })
    },
    onError: (error) => notifications.show({
      color: 'red', title: 'Could not create teams',
      message: error.response?.data?.error || 'Please try again.',
    }),
  })

  if (isLoading) {
    return <Center py={80}><Loader color="grape" /></Center>
  }
  if (isError || !group) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="red.4" fw={600} ff="Orbitron, sans-serif">Not a facilitator for this group</Text>
          <Button onClick={() => navigate('/')}>Home</Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2} ff="Orbitron, sans-serif" c="grape.3">{group.name.trim()}</Title>
          <Text c="dimmed" size="sm">{group.members.length} members · {teamSummaries.length} teams</Text>
        </div>

        {/* Navigation to the per-story thread-gating screen. These are LINKS,
            not release triggers — releasing happens on the destination page.
            Always rendered so the thread gates are reachable; shows an explicit
            empty state when no story is attached to the group. */}
        <Paper p="md" withBorder>
          <Text fw={600} mb={4}>Thread gates</Text>
          {gateableStories.length > 0 ? (
            <>
              <Text size="xs" c="dimmed" mb="xs">
                Open a story to control which sessions are released to its teams.
              </Text>
              <Group gap="xs">
                {gateableStories.map((s) => (
                  <Button
                    key={s.id}
                    variant="light"
                    color="cyan"
                    size="xs"
                    leftSection={<IconCalendarEvent size={14} />}
                    rightSection={<IconArrowRight size={14} />}
                    onClick={() => navigate(`/facilitate/${s.id}`)}
                  >
                    Manage thread gates: {s.name}
                  </Button>
                ))}
              </Group>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              No story is attached to this group yet, so there are no sessions to
              release. Attach a story to the group (in admin) to manage thread gates.
            </Text>
          )}
        </Paper>

        {/* Create teams */}
        <Paper p="md" withBorder>
          <Text fw={600} mb="xs">Create teams</Text>
          <Group align="flex-end" gap="md">
            <NumberInput label="How many teams" value={count} onChange={setCount} min={1} max={50} w={140} />
            <Switch label="Distribute roster evenly" checked={distribute} onChange={(e) => setDistribute(e.currentTarget.checked)} />
            <Button leftSection={<IconUsersPlus size={16} />} loading={create.isPending} onClick={() => create.mutate()}>
              Create
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mt={6}>
            Teams are created without starting a story; start one per team below when ready.
          </Text>
        </Paper>

        {/* Group-wide role limits (primary control; teams inherit unless overridden) */}
        <GroupRoleLimits group={group} groupKey={groupKey} teamIds={teamSummaries.map((t) => t.id)} />

        {/* Teams */}
        <Divider label="Teams" labelPosition="left" />
        {teamSummaries.length === 0 && (
          <Text c="dimmed" ta="center" py="lg">No teams yet. Create some above.</Text>
        )}
        <Stack gap="md">
          {teamDetails.map((team) => {
            const siblings = teamDetails.filter((t) => t.id !== team.id)
            return (
              <div key={team.id}>
                {!team.current_story && (
                  <StartStoryBar
                    teamId={team.id}
                    teamName={team.name}
                    stories={stories}
                    onStarted={() => {
                      queryClient.invalidateQueries({ queryKey: ['team', team.id] })
                      queryClient.invalidateQueries({ queryKey: groupKey })
                    }}
                  />
                )}
                <TeamManagementPanel team={team} siblingTeams={siblings} invalidateKey={groupKey} />
              </div>
            )
          })}
        </Stack>
      </Stack>
    </Container>
  )
}

/**
 * Group-wide role-limit editor. The PRIMARY place to set role quotas: values
 * here apply to every team in the group, and a team only diverges if its
 * facilitator explicitly overrides it (see TeamManagementPanel). Roles are the
 * union of player_roles across the group's stories; current values come from
 * `group.role_caps`. On save we invalidate the group query AND every team query
 * so each team's inherited/effective caps refresh.
 */
function GroupRoleLimits({ group, groupKey, teamIds }) {
  const queryClient = useQueryClient()

  // Union of player_roles across the group's stories, de-duped by key. Most
  // groups run a single story, but a group can span several.
  const roles = []
  const seen = new Set()
  for (const s of group.stories || []) {
    for (const r of s.player_roles || []) {
      if (r?.key && !seen.has(r.key)) { seen.add(r.key); roles.push(r) }
    }
  }
  // Local working copy so typing feels immediate; seeded from saved group caps.
  const savedCaps = group.role_caps || {}
  const [caps, setCaps] = useState(savedCaps)

  const save = useMutation({
    mutationFn: (next) => facilitatorService.setGroupRoleCaps(group.id, next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKey })
      teamIds.forEach((id) => queryClient.invalidateQueries({ queryKey: ['team', id] }))
      notifications.show({ color: 'green', message: 'Group role limits updated' })
    },
    onError: (error) => notifications.show({
      color: 'red', title: 'Could not update limits',
      message: error.response?.data?.error || 'Please try again.',
    }),
  })

  const updateCap = (roleKey, value) => {
    // 0/empty = unlimited. Merge so other roles keep their values.
    const next = { ...caps, [roleKey]: value === '' || value == null ? 0 : Number(value) }
    setCaps(next)
    save.mutate(next)
  }

  if (roles.length === 0) return null

  return (
    <Paper p="md" withBorder>
      <Text fw={600} mb={4}>Role limits (group default)</Text>
      <Text size="xs" c="dimmed" mb="sm">
        Applies to every team unless a team overrides it. 1 = unique (default). 0 = unlimited.
      </Text>
      <Group gap="md" wrap="wrap">
        {roles.map((role) => {
          const cap = role.key in caps ? Number(caps[role.key]) : 1
          return (
            <Stack key={role.key} gap={4} style={{ minWidth: 150 }}>
              <Text size="sm" fw={600}>{role.name}</Text>
              <NumberInput
                size="xs"
                min={0}
                max={99}
                value={cap <= 0 ? 0 : cap}
                onChange={(v) => updateCap(role.key, v)}
                disabled={save.isPending}
                data-testid={`group-cap-${role.key}`}
              />
            </Stack>
          )
        })}
      </Group>
    </Paper>
  )
}

function StartStoryBar({ teamId, teamName, stories, onStarted }) {
  const [storyId, setStoryId] = useState(stories.length === 1 ? stories[0].id : null)
  const start = useMutation({
    mutationFn: () => teamService.addStory(teamId, storyId),
    onSuccess: () => {
      notifications.show({ color: 'green', message: `Started for ${teamName}` })
      onStarted?.()
    },
    onError: (error) => notifications.show({
      color: 'red', title: 'Could not start story',
      message: error.response?.data?.error || 'Please try again.',
    }),
  })

  return (
    <Paper p="sm" withBorder mb={4} style={{ borderColor: 'var(--mantine-color-yellow-9)' }}>
      <Group gap="sm" align="flex-end">
        <Text size="sm" c="yellow.4">{teamName} hasn’t started a story.</Text>
        <Select
          placeholder="Pick a story"
          data={stories.map((s) => ({ value: s.id, label: s.name }))}
          value={storyId}
          onChange={setStoryId}
          w={220}
          size="xs"
        />
        <Button
          size="xs"
          color="yellow"
          leftSection={<IconPlayerPlay size={14} />}
          disabled={!storyId}
          loading={start.isPending}
          onClick={() => start.mutate()}
        >
          Start story
        </Button>
      </Group>
    </Paper>
  )
}
